import { Mail, Phone, Download, ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "@/lib/site";
import useReveal from "@/lib/useReveal";

export default function Team() {
  useReveal();
  const p = SITE.team.principle;

  return (
    <main data-testid="page-team">
      <section className="pt-16 md:pt-20 pb-10 md:pb-14 bg-gradient-to-b from-navy-50/40 to-transparent">
        <div className="container-narrow">
          <div className="text-[12px] tracking-[0.22em] uppercase text-gold-500 font-medium reveal">
            Meet the Team
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl text-navy-800 mt-3 leading-[1.04] tracking-tight reveal">
            Meet the St. John Accounting & Tax Team.
          </h1>
          <p className="mt-5 max-w-3xl text-slate-600 text-[17px] leading-relaxed reveal">
            Experienced advisors who serve with professionalism, integrity, and diligence. Helping you make better, informed decisions.
          </p>
        </div>
      </section>

      {/* David's profile */}
      <section className="py-12 md:py-20" data-testid="team-principle">
        <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="rounded-3xl overflow-hidden border border-navy-100 shadow-[0_30px_80px_-40px_rgba(10,32,70,0.3)] bg-white">
              <img
                src={p.photo}
                alt={`${p.name}, ${p.title}`}
                className="w-full h-auto object-cover"
              />
              <div className="p-6">
                <div className="text-[12px] tracking-[0.22em] uppercase text-gold-500 font-medium">
                  Principal Advisor
                </div>
                <div className="font-serif-display text-2xl text-navy-800 mt-1 font-semibold">
                  {p.name}
                </div>
                <div className="text-slate-500 text-[14px] mt-1">MBA, EA, MS<sup className="text-[10px]">Acct/Tax</sup>, CFP<sup className="text-[9px]">®</sup></div>
                <div className="mt-5 flex flex-col gap-2 text-[14.5px]">
                  <a
                    href={SITE.phoneTel}
                    data-testid="team-david-phone"
                    className="inline-flex items-center gap-2 text-slate-700 hover:text-navy-800 transition-colors"
                  >
                    <Phone size={15} className="text-gold-500" /> {p.phone}
                  </a>
                  <a
                    href={`mailto:${p.email}`}
                    data-testid="team-david-email"
                    className="inline-flex items-center gap-2 text-slate-700 hover:text-navy-800 transition-colors break-all"
                  >
                    <Mail size={15} className="text-gold-500" /> {p.email}
                  </a>
                  <a
                    href={p.resume}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="team-david-resume"
                    className="inline-flex items-center gap-2 text-navy-800 font-medium hover:text-navy-700 transition-colors"
                  >
                    <Download size={15} /> Professional Resume (PDF)
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <h2 className="font-serif-display text-3xl md:text-5xl text-navy-800 leading-[1.06]">
              David St. John
            </h2>
            <div className="mt-3 flex items-center gap-3 text-slate-500 text-[14px]">
              <span className="h-px w-10 bg-gold-500" />
              <span>20+ years of experience</span>
            </div>
            <div className="mt-7 space-y-5 text-slate-600 text-[16.5px] leading-relaxed">
              {SITE.team.bio.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              <p>
                David has served clients in the financial services industry for
                over four decades. His greatest professional passion is
                being a trusted advisor to small business owners and families
                who seek a holistic, balanced, and financially secure lifestyle.
                Dedicated to integrity and excellence, David consistently
                strives to deliver the highest level of service to his clients.
              </p>
              <p>
                David's secondary professional passion is to create a positive, progressive, and peaceful work environment where team members can develop exceptional professional and interpersonal skills that will have a positive impact on their clients, families, and communities.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="team-book-cta"
                className="btn-primary"
              >
                <Calendar size={16} /> Book with David
              </a>
              <Link to="/contact" data-testid="team-contact-cta" className="btn-secondary">
                Send a message <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Associate advisors */}
      <section className="py-16 md:py-20 bg-navy-50/50 border-y border-navy-100" data-testid="team-associates">
        <div className="container-narrow">
          <div className="text-[12px] tracking-[0.22em] uppercase text-gold-500 font-medium reveal">
            Associate Advisors
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-navy-800 mt-3 leading-[1.1] reveal">
            The team that supports every client relationship.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {SITE.team.associates.map((a) => (
              <div
                key={a.email}
                data-testid={`team-associate-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="reveal card-lift rounded-2xl bg-white border border-navy-100 p-6"
              >
                <img
                  src={a.photo}
                  alt={`Portrait of ${a.name}`}
                  loading="lazy"
                  width="240"
                  height="320"
                  data-testid={`team-associate-photo-${a.name.split(" ")[0].toLowerCase()}`}
                  className="w-full aspect-[3/4] object-cover object-top rounded-xl bg-navy-50"
                />
                <div className="font-serif-display text-[19px] text-navy-800 mt-4 font-semibold">
                  {a.name}
                </div>
                <a
                  href={`mailto:${a.email}`}
                  className="mt-2 inline-flex items-start gap-2 text-[14px] text-slate-600 hover:text-navy-800 transition-colors"
                >
                  <Mail size={14} className="text-gold-500 mt-1 shrink-0" />
                  <span>{a.email.split("@")[0]}<wbr />@{a.email.split("@")[1]}</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
